import { useEffect, useState } from 'react';
import { publicAttendanceLocationSchema, PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS } from 'shared-models/browser';
import type { PublicAttendanceLocation } from 'shared-models/browser';

const permissionMessage = 'הגישה למיקום נחסמה. יש לאפשר מיקום בהגדרות האתר והמערכת';
const unsupportedMessage = 'הדפדפן אינו תומך במיקום. יש להשתמש בדפדפן עדכני או בטלפון';
const staleMessage = 'המיקום שהתקבל אינו עדכני. יש לעדכן מיקום ולנסות שוב';
const options: PositionOptions = { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 };
const fallbackOptions: PositionOptions = { ...options, enableHighAccuracy: false };

function failureMessage(error: GeolocationPositionError): string {
  if (error.code === 1) return permissionMessage;
  if (error.code === 3) return 'קבלת המיקום נמשכה זמן רב מדי. נסו לעדכן מיקום או להיכנס מהטלפון';
  return 'לא ניתן לקבל מיקום מהמחשב כרגע. יש לבדוק את שירותי המיקום והחיבור לרשת, או לנסות מהטלפון';
}

function readPosition(position: GeolocationPosition): PublicAttendanceLocation {
  const parsed = publicAttendanceLocationSchema.safeParse({ latitude: position.coords.latitude, longitude: position.coords.longitude,
    accuracy: position.coords.accuracy, timestamp: position.timestamp });
  if (!parsed.success) throw new Error('לא התקבל מיקום תקין. יש לעדכן מיקום ולנסות שוב');
  if (Math.abs(Date.now() - position.timestamp) > PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS) throw new Error(staleMessage);
  return parsed.data;
}

export function usePublicAttendanceLocation() {
  const [location, setLocation] = useState<PublicAttendanceLocation | null>(null);
  const [locationError, setLocationError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    let expiry: ReturnType<typeof setTimeout>;
    let permission: PermissionStatus | undefined;
    let blocked = false;
    let fallbackUsed = false;
    let fallbackPending = false;
    let lastPosition: PublicAttendanceLocation | null = null;
    setLocation(null); setLocationError('');
    const unavailable = (message: string) => {
      if (active) { clearTimeout(expiry); lastPosition = null; setLocation(null); setLocationError(message); }
    };
    if (!navigator.geolocation) { unavailable(unsupportedMessage); return; }
    const located = (position: GeolocationPosition) => {
      if (!active || blocked) return;
      try {
        const fresh = readPosition(position);
        lastPosition = fresh;
        clearTimeout(expiry); setLocation(fresh); setLocationError('');
        expiry = setTimeout(() => unavailable(staleMessage), Math.max(0, fresh.timestamp + PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS - Date.now()));
      } catch (error) { unavailable(error instanceof Error ? error.message : staleMessage); }
    };
    const rejected = (error: GeolocationPositionError) => {
      if (!active) return;
      if (error.code === 1) { blocked = true; unavailable(permissionMessage); return; }
      if (blocked || fallbackPending || (lastPosition && Date.now() - lastPosition.timestamp <= PUBLIC_ATTENDANCE_LOCATION_MAX_AGE_MS)) return;
      if (fallbackUsed) { unavailable(failureMessage(error)); return; }
      fallbackUsed = true; fallbackPending = true;
      navigator.geolocation.getCurrentPosition((position) => {
        fallbackPending = false; located(position);
      }, (failure) => {
        fallbackPending = false;
        if (!active || blocked) return;
        if (failure.code === 1) blocked = true;
        if (lastPosition && failure.code !== 1) return;
        unavailable(failureMessage(failure));
      }, fallbackOptions);
    };
    const watcher = navigator.geolocation.watchPosition(located, rejected, options);
    void navigator.permissions?.query({ name: 'geolocation' }).then((status) => {
      if (!active) return;
      permission = status;
      status.onchange = () => { blocked = status.state === 'denied'; if (blocked) unavailable(permissionMessage); };
      if (status.state === 'denied') { blocked = true; unavailable(permissionMessage); }
    }).catch(() => undefined);
    return () => { active = false; clearTimeout(expiry); navigator.geolocation.clearWatch(watcher); if (permission) permission.onchange = null; };
  }, [attempt]);

  const currentLocation = () => new Promise<PublicAttendanceLocation>((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error(unsupportedMessage)); return; }
    let fallbackUsed = false;
    const located = (position: GeolocationPosition) => {
      try { resolve(readPosition(position)); } catch (error) { setLocation(null); setLocationError(staleMessage); reject(error); }
    };
    const rejected = (error: GeolocationPositionError) => {
      if (error.code !== 1 && !fallbackUsed) {
        fallbackUsed = true; navigator.geolocation.getCurrentPosition(located, rejected, fallbackOptions); return;
      }
      const message = failureMessage(error);
      setLocation(null); setLocationError(message); reject(new Error(message));
    };
    navigator.geolocation.getCurrentPosition(located, rejected, options);
  });
  const isSafari = /Safari\//.test(navigator.userAgent) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|OPiOS|Android/.test(navigator.userAgent);
  const isAppleMobile = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  const locationInstructions = locationError === permissionMessage && isSafari
    ? isAppleMobile ? [
      'הגדרות > פרטיות ואבטחה > שירותי מיקום > אתרי Safari (Safari Websites): בחרו "בעת השימוש ביישום" והפעילו "מיקום מדויק".',
      'ב-Safari פתחו את תפריט הדף (aA או תפריט הדף > עוד, לפי הגרסה), בחרו "הגדרות אתר" > "מיקום" > "אפשר".',
    ] : [
      'הגדרות המערכת > פרטיות ואבטחה > שירותי מיקום: הפעילו שירותי מיקום ואפשרו גישה ל-Safari.',
      'Safari > הגדרות > אתרי אינטרנט > מיקום: ליד האתר הזה בחרו "אפשר".',
    ] : [];
  return { location, locationError, locationInstructions, currentLocation, retryLocation: () => setAttempt((previous) => previous + 1) };
}